import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { Media } from 'app/theme/media'
import React, { memo, useEffect, useState } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'
import { Parallax } from 'react-scroll-parallax'

import { Accordions } from './Accordions'
import { BackTo } from './BackTo'
import { Buttons } from './Buttons'
import { Price } from './Price'

export interface Props {
  accordionTitle?: string
  accordionDescription?: string
  bookingButtonURL?: string
  category?: string
  contactsEmail?: string
  cta?: ButtonProps
  description?: string
  includes?: string
  image?: ImageProps
  languageCode: string
  experiencesPageURL?: string
  price?: string
  requestButtonURL?: string
  subtitle?: string
  terms?: string
  title: string
}

export const ExperiencesIntro = memo(function ExperiencesIntro({
  accordionTitle,
  accordionDescription,
  bookingButtonURL,
  category,
  contactsEmail,
  cta,
  description,
  includes,
  image,
  languageCode,
  experiencesPageURL,
  price,
  requestButtonURL,
  subtitle,
  terms,
  title,
}: Props) {
  const [loading, setloading] = useState(true)

  useEffect(() => {
    setTimeout(() => {
      setloading(false)
    }, 2000)
  }, [])

  return (
    <Container>
      <Media greaterThanOrEqual="desktopSmall">
        <BackTo
          languageCode={languageCode}
          experiencesPageURL={experiencesPageURL}
        />
      </Media>

      <Wrapper row wrap>
        <LeftSide>
          {category ? (
            <FadeInUp delay={loading ? 1.5 : 0}>
              <Category>{category}</Category>
            </FadeInUp>
          ) : null}

          {title ? (
            <Title delay={loading ? 1.7 : 0} length={30} text={title} />
          ) : null}

          {subtitle ? (
            <FadeInUp delay={loading ? 1.9 : 0}>
              <Subtitle>{subtitle}</Subtitle>
            </FadeInUp>
          ) : null}

          {price ? <Price price={price} /> : null}

          {description ? (
            <FadeInUp delay={loading ? 2.3 : 0}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}

          {cta ? (
            <FadeInUp delay={loading ? 2.5 : 0}>
              <CTA variant="simple" {...cta} />
            </FadeInUp>
          ) : null}

          {includes ? (
            <FadeInUp delay={loading ? 2.5 : 0}>
              <Accordions
                description={includes}
                index="0"
                isExpanded="0"
                title="includes"
              />
            </FadeInUp>
          ) : null}

          {terms ? (
            <FadeInUp delay={loading ? 2.7 : 0}>
              <Accordions
                description={terms}
                index="1"
                title="terms-conditions"
              />
            </FadeInUp>
          ) : null}

          {accordionTitle && accordionDescription ? (
            <FadeInUp delay={loading ? 3.1 : 0}>
              <Accordions
                description={accordionDescription}
                index="2"
                title={accordionTitle}
              />
            </FadeInUp>
          ) : null}

          <Buttons
            bookingButtonURL={bookingButtonURL}
            contactsEmail={contactsEmail}
            languageCode={languageCode}
            requestButtonURL={requestButtonURL}
          />
        </LeftSide>

        <RightSide>
          {image ? (
            <LazyLoadComponent>
              <DropDown delay={1.5}>
                <Parallax translateY={['-20%', '20%']}>
                  <Image {...image} />
                </Parallax>
              </DropDown>
            </LazyLoadComponent>
          ) : null}
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  padding-top: 7.5rem;

  @media (max-width: 1199px) {
    margin-bottom: 5.625rem;
    padding-top: 3.75rem;
    position: relative;
  }
`

const Wrapper = styled(FlexBox)`
  margin-top: 4.0625rem;

  @media (max-width: 1199px) {
    margin-top: 0;
  }
`

const LeftSide = styled.div`
  width: 55%;
  padding: 0 6.944vw 8.125rem 10vw;

  @media (max-width: 1199px) {
    width: 100%;
    order: 2;
    padding: 0 1.875rem;
  }
`

const Category = styled.div`
  display: inline-block;
  border: 0.0625rem solid ${({ theme }) => theme.colors.variants.neutralLight3};
  border-radius: 0.9375rem;
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 0.5625rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  line-height: 0.6875rem;
  margin-bottom: 0.75rem;
  padding: 0.5625rem 1rem;
  text-transform: uppercase;
`

const Title = styled(AnimatedTitle)`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.25rem;
  font-weight: 500;
  line-height: 2.0625rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1.875rem;
    line-height: 1.875rem;
  }
`

const Subtitle = styled.h2`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 300;
  line-height: 1.875rem;
  margin-top: 0.25rem;

  @media (max-width: 1199px) {
    font-size: 1.125rem;
    line-height: 1.5rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin: 1.875rem 0 3rem;
`

const CTA = styled(Button)`
  margin-bottom: 3rem;

  @media (max-width: 1199px) {
    margin-bottom: 1.875rem;
  }
`

const RightSide = styled.div`
  width: 45%;
  aspect-ratio: 0.98;
  position: relative;
  padding-bottom: 8.125rem;

  > div {
    height: 100%;
    overflow: hidden;
    > div {
      height: 120%;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: 1199px) {
    padding-bottom: 0;
    aspect-ratio: 1.33;
    width: 100%;

    > div {
      transform: translateY(-10%);
    }
  }
`
